import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '../../services/logger/logger';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ClientSideAgGrid from '../../components/common/customAgGrid/ClientSideAgGrid';
import CustomAgDateFilter from '../../components/common/customAgGrid/CustomAgDateFilter';
import CustomAgMultiSelectFilter from '../../components/common/customAgGrid/CustomAgMultiSelectFilter';
import CustomAgTextFilter from '../../components/common/customAgGrid/CustomAgTextFilter';
import Stack from '@mui/material/Stack';
import PageHeader, { ActionItem } from '../../components/PageHeader';
import CheckIssuesModal from '../../components/priceLists/CheckIssuesModal';
import ImportPriceListModal from '../../components/priceLists/ImportPriceListModal';
import ImportValidationModal from '../../components/priceLists/ImportValidationModal';
import { useGetCompaniesCustomerListQuery } from '../../store/apiSlices/companies/companiesCustomer/companiesCustomerApis';
import { useGetCompaniesSuppliersListQuery } from '../../store/apiSlices/companies/companiesSuppliers/companiesSuppliersApis';
import {
  useGetPriceListQuery,
  useLazyGetTempPriceListsQuery,
  useProcessValidRecordsMutation,
  useUploadPriceListMutation,
} from '../../store/apiSlices/priceLists/priceListApis';
import { dateRangeFilterPredicate } from '../../utils/agGridPredicates';
import { canEditReferenceData } from '../../utils/permissions';
import { showSuccessToast } from '../../components/common/BaseToaster';
import { TableCardContent } from '../../components/common/styled/TableCardContent';

const PriceListContainer = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const canAddorEdit = canEditReferenceData(userInfo?.authorityName);
  const navigate = useNavigate();
  const [openImportModal, setOpenImportModal] = useState(false);
  const [openImportValidationModal, setOpenImportValidationModal] = useState(false);
  const [uploadSummary, setUploadSummary] = useState<any | null>(null);
  const [openCheckIssuesModal, setOpenCheckIssuesModal] = useState(false);
  const [issuesRows, setIssuesRows] = useState<
    Array<{ rowNo: number | string; column: string; error: string }>
  >([]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [resetFiltersSignal, setResetFiltersSignal] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      customer: '',
      supplier: '',
      documentId: '',
      validityDateRange: undefined as any,
      file: [],
    },
  });
  const [uploadPriceList] = useUploadPriceListMutation();
  const [processValidRecords] = useProcessValidRecordsMutation();
  const [fetchTempPriceLists] = useLazyGetTempPriceListsQuery();

  const {
    data: priceListData,
    isLoading: isLoadingPriceList,
    refetch: refetchPriceList,
  } = useGetPriceListQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  // Load dropdown options only when import modal is open
  const { data: customersList } = useGetCompaniesCustomerListQuery({ active: true }, {
    skip: !openImportModal,
  } as any);
  const { data: suppliersList } = useGetCompaniesSuppliersListQuery(
    undefined as any,
    { skip: !openImportModal } as any,
  );

  const customerOptions = Array.isArray(customersList)
    ? customersList.map((c: any) => ({
        value: c.id,
        label: c.shortName || c.customer,
      }))
    : [];
  const supplierOptions = Array.isArray(suppliersList)
    ? suppliersList.map((s: any) => ({
        value: s.id,
        label: s.shortName || s.supplier,
      }))
    : [];

  const breadcrumbOptions = [
    {
      label: t('priceList.title'),
      isActive: true,
      onClick: () => {},
    },
  ];

  const actions: ActionItem[] = canAddorEdit
    ? [
        {
          label: t('buttons.import'),
          onClick: () => {
            // Set today's date as default for startDate only
            const today = new Date();
            reset({
              customer: '',
              supplier: '',
              documentId: '',
              validityDateRange: {
                startDate: today,
                endDate: undefined, // Don't set endDate by default
              },
              file: [],
            });
            setOpenImportModal(true);
          },
          variant: 'blue',
        },
      ]
    : [];

  const columns: any[] = [
    {
      field: 'srno',
      headerName: t('table.columns.id'),
      sortable: true,
      width: 120,
      maxWidth: 120,
      flex: 0,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomAgTextFilter,
    },
    {
      field: 'customer',
      headerName: t('table.columns.customer'),
      sortable: true,
      width: 120,
      tooltipThreshold: 20,
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomAgMultiSelectFilter,
    },
    {
      field: 'supplier',
      headerName: t('table.columns.supplier'),
      sortable: true,
      width: 120,
      tooltipThreshold: 20,
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomAgMultiSelectFilter,
    },
    {
      field: 'currency',
      headerName: t('table.columns.currency'),
      sortable: true,
      width: 120,
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomAgMultiSelectFilter,
    },
    {
      field: 'validityDateRange',
      headerName: t('table.columns.validityDateRange'),
      sortable: true,
      width: 250,
      filter: 'agDateColumnFilter',
      floatingFilter: true,
      floatingFilterComponent: CustomAgDateFilter,
      filterParams: {
        maxNumConditions: 1, // hide AND/OR
        suppressAndCondition: true,
        filterOptions: [
          {
            displayKey: 'inRange',
            displayName: t('common.between'),
            numberOfInputs: 2,
            predicate: dateRangeFilterPredicate,
          },
        ],
      },
    },
  ];

  const onImport = async (values: any) => {
    const currencyId = 19; // USD fixed
    const customerId = Number(values?.customer);
    const supplierId = Number(values?.supplier);
    const documentNb = String(values?.documentId || '');
    const fileInput = values?.file;
    const file: File | undefined = Array.isArray(fileInput) ? fileInput[0] : fileInput;
    const range = values?.validityDateRange || {};
    const fmt = (d: Date) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };
    const dateFrom = range?.startDate ? fmt(new Date(range.startDate)) : '';
    const dateTo = range?.endDate ? fmt(new Date(range.endDate)) : '';

    if (!customerId || !supplierId || !documentNb || !file || !dateFrom) {
      // minimal guard; UI may show toaster elsewhere
      return;
    }

    try {
      const res = await uploadPriceList({
        customerId,
        supplierId,
        currencyId,
        dateFrom,
        dateTo,
        documentNb,
        file,
      }).unwrap();
      showSuccessToast(res?.message);

      // Save document ID to localStorage for later retrieval
      // Create a unique key based on customer, supplier, and date range
      const storageKey = `priceList_doc_${customerId}_${supplierId}_${dateFrom}_${dateTo}`;
      try {
        localStorage.setItem(storageKey, documentNb);
      } catch (storageError) {
        logger.error('Failed to save document ID to localStorage', storageError as Error);
      }

      setUploadSummary(res);
      setOpenImportModal(false);
      setOpenImportValidationModal(true);
    } catch (e) {
      // keep modal open for user to fix
    }
  };

  const onImportValidEntries = async () => {
    const tempId = uploadSummary?.data?.tempPriceListId;
    if (!tempId) {
      setOpenImportValidationModal(false);
      return;
    }
    try {
      const res: any = await processValidRecords({
        tempPriceListId: tempId,
      }).unwrap();
      showSuccessToast(res?.message);

      // Refetch price list to show newly imported data
      await refetchPriceList();
    } catch (e) {
      // ignore, UI may show global error
    } finally {
      setOpenImportValidationModal(false);
    }
  };

  const onCheckIssues = async () => {
    setOpenImportValidationModal(false);
    try {
      const res: any = await fetchTempPriceLists({ isValid: false }).unwrap();
      showSuccessToast(res?.message);
      const list = Array.isArray(res?.data) ? res?.data : [];
      const products = list.flatMap((item: any) =>
        Array.isArray(item.products) ? item.products : [],
      );
      const rows = products.map((p: any) => ({
        rowNo: p.rowNumber,
        column: String(p.errorColumns || '-'),
        error: String(p.errorMessage || '-'),
      }));
      setIssuesRows(rows);
    } catch (e) {
      setIssuesRows([]);
    }
    setOpenCheckIssuesModal(true);
  };

  const handleCloseImportModal = () => {
    reset({
      customer: '',
      supplier: '',
      documentId: '',
      validityDateRange: undefined,
      file: [],
    });
    setOpenImportModal(false);
  };

  const onUploadUpdatedFile = () => {
    // Do not reset here; preserve user's previous inputs for correction
    setOpenImportModal(true);
    setOpenCheckIssuesModal(false);
  };

  return (
    <Stack gap={1} sx={{ height: '100%', minHeight: 0 }}>
      <PageHeader
        options={breadcrumbOptions}
        actions={actions}
        isFilterActive={isFilterActive}
        resetFilters={() => setResetFiltersSignal(true)}
      />
      <TableCardContent>
        <ClientSideAgGrid
          columnDefs={columns as any}
          rowData={priceListData}
          loading={isLoadingPriceList}
          className="price-list-grid"
          onCellClicked={(params: any) => {
            if (params?.data) {
              navigate('/price-list-details', {
                state: { data: params.data },
              });
            }
          }}
          onFiltersActiveChange={(active: boolean) => {
            setIsFilterActive(active);
            setResetFiltersSignal(false);
          }}
          resetFiltersSignal={resetFiltersSignal}
        />
      </TableCardContent>

      {openImportModal && (
        <ImportPriceListModal
          open={openImportModal}
          onClose={handleCloseImportModal}
          handleSubmit={handleSubmit(onImport)}
          control={control}
          errors={errors}
          customerOptions={customerOptions}
          supplierOptions={supplierOptions}
        />
      )}

      <ImportValidationModal
        open={openImportValidationModal}
        onClose={() => setOpenImportValidationModal(false)}
        onImportValidEntries={onImportValidEntries}
        onCheckIssues={onCheckIssues}
        summary={
          uploadSummary?.data
            ? {
                totalRecords: uploadSummary?.data?.totalRecords,
                validRecords: uploadSummary?.data?.validRecords,
                invalidRecords: uploadSummary?.data?.invalidRecords,
              }
            : undefined
        }
      />

      <CheckIssuesModal
        open={openCheckIssuesModal}
        onClose={() => setOpenCheckIssuesModal(false)}
        onUploadUpdatedFile={onUploadUpdatedFile}
        onBack={() => {
          setOpenCheckIssuesModal(false);
          setOpenImportValidationModal(true);
        }}
        issuesRows={issuesRows}
      />
    </Stack>
  );
};

export default PriceListContainer;
